import { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { useSettings } from "../contexts/SettingsContext";

/**
 * AnimatedNumber - Counts up from 0 to value with formatting.
 * Waits until the element is visible in the viewport AND has a non-zero value
 * before triggering the animation, so users always see the count-up.
 *
 * Respects two levels of settings:
 * 1. showAnimatedNumbers — global master toggle
 * 2. animatedNumberSections — per-section granular control
 *
 * Sections: "dashboardCards", "chartTotals", "statsCards", "tables",
 *           "sourceDetails", "admin"
 */
const AnimatedNumber = ({
  value,
  prefix = "",
  suffix = "",
  decimals,
  duration = 2,
  separator = ",",
  isCurrency = false,
  section,
}) => {
  const numValue = Number(value) || 0;
  const ref = useRef(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { showAnimatedNumbers, animatedNumberSections } = useSettings();

  const resolvedPrefix = isCurrency ? "$" : prefix;
  const resolvedDecimals =
    decimals !== undefined
      ? decimals
      : isCurrency
        ? 2
        : numValue % 1 !== 0
          ? 2
          : 0;

  // Check if animation is enabled for this section
  const isEnabled =
    showAnimatedNumbers &&
    (!section || animatedNumberSections?.[section] !== false);

  const formatStatic = (n) => {
    const fixed = n.toFixed(resolvedDecimals);
    const [intPart, decPart] = fixed.split(".");
    const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return (
      resolvedPrefix + (decPart ? `${withSep}.${decPart}` : withSep) + suffix
    );
  };

  useEffect(() => {
    if (!isEnabled || shouldAnimate || numValue === 0) return;

    const el = ref.current;
    if (!el) {
      setShouldAnimate(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [numValue, shouldAnimate, isEnabled]);

  if (!isEnabled) {
    return <span>{formatStatic(numValue)}</span>;
  }

  if (!shouldAnimate) {
    return <span ref={ref}>{formatStatic(0)}</span>;
  }

  return (
    <CountUp
      end={numValue}
      prefix={resolvedPrefix}
      suffix={suffix}
      decimals={resolvedDecimals}
      duration={duration}
      separator={separator}
      useEasing
      easingFn={(t, b, c, d) => {
        const x = t / d - 1;
        return c * (x * x * x + 1) + b;
      }}
      preserveValue
    />
  );
};

export default AnimatedNumber;
